import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiEye, FiEyeOff, FiLock, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import type { CarrierDefinition } from './carrierAccessCatalog';

interface CarrierCredentialModalProps {
  carrier: CarrierDefinition;
  onClose: () => void;
  onSubmit: (username: string, password: string) => Promise<void>;
}

const CarrierCredentialModal: React.FC<CarrierCredentialModalProps> = ({ carrier, onClose, onSubmit }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!username.trim() || !password.trim()) {
      setError('Enter both a username and password.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSubmit(username.trim(), password);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="ca-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={saving ? undefined : onClose}
    >
      <motion.div
        className="ca-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ca-modal-title"
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ca-modal__header">
          <span
            className={`ca-modal__logo ${carrier.logoUrl ? 'ca-modal__logo--image' : ''}`}
            style={{ background: carrier.logoUrl ? (carrier.logoBg ?? '#fff') : carrier.color }}
          >
            {carrier.logoUrl ? <img src={carrier.logoUrl} alt={carrier.name} /> : carrier.name.charAt(0)}
          </span>
          <h3 id="ca-modal-title">Connect {carrier.name}</h3>
          <button type="button" className="ca-modal__close" onClick={onClose} disabled={saving} aria-label="Close">
            <FiX size={16} />
          </button>
        </div>

        <p className="ca-modal__desc">
          Enter the same username and password you use to sign in to your {carrier.name} agent portal.
          Momentum AMP uses this only to run the actions you turn on — it's never shared with other agencies.
        </p>

        <label className="ca-modal__label" htmlFor="ca-username">Portal Username</label>
        <div className="ca-modal__input-wrap">
          <input
            id="ca-username"
            className="ca-modal__input"
            placeholder={`Your ${carrier.name} username`}
            value={username}
            autoComplete="off"
            disabled={saving}
            onChange={(e) => { setUsername(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSubmit(); }}
          />
        </div>

        <label className="ca-modal__label" htmlFor="ca-password" style={{ marginTop: 12 }}>Portal Password</label>
        <div className="ca-modal__input-wrap">
          <input
            id="ca-password"
            type={showPassword ? 'text' : 'password'}
            className="ca-modal__input"
            placeholder={`Your ${carrier.name} password`}
            value={password}
            autoComplete="new-password"
            disabled={saving}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleSubmit(); }}
          />
          <button
            type="button"
            className="ca-modal__eye-btn"
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
          </button>
        </div>

        {error && (
          <div className="ca-modal__error">
            <FiAlertTriangle size={12} /> {error}
          </div>
        )}

        <div className="ca-modal__actions">
          <button type="button" className="ca-modal__btn ca-modal__btn--secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="ca-modal__btn ca-modal__btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <FiRefreshCw size={14} className="spin" /> Connecting…
              </>
            ) : (
              <>
                <FiLock size={14} /> Connect
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CarrierCredentialModal;
